import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { useT } from "@/i18n";

const blank = (subs) => Object.fromEntries(subs.map((_, i) => [i, ""]));

export const composeEntries = (entries, subs) => {
  const filled = entries.filter((e) => Object.values(e).some((v) => (v || "").trim()));
  if (subs.length === 1) return filled.map((e) => (e[0] || "").trim()).filter(Boolean).join(", ");
  return filled
    .map((e) => {
      const head = (e[0] || "").trim();
      const rest = subs.slice(1).map((s, i) => ((e[i + 1] || "").trim() ? `${s.label}: ${e[i + 1].trim()}` : "")).filter(Boolean);
      return [head ? `**${head}**` : "", ...rest].filter(Boolean).join(", ");
    })
    .join("\n");
};

export function ListField({ subfields, onChange, invalid, testId, compact = false }) {
  const { t } = useT();
  const subs = subfields?.length ? subfields : [{ label: "", input: "text" }];
  const [entries, setEntries] = useState([blank(subs)]);

  const update = (next) => { setEntries(next); onChange(composeEntries(next, subs)); };
  const set = (idx, si, v) => update(entries.map((e, i) => (i === idx ? { ...e, [si]: v } : e)));
  const add = () => update([...entries, blank(subs)]);
  const remove = (idx) => update(entries.length > 1 ? entries.filter((_, i) => i !== idx) : [blank(subs)]);

  return (
    <div data-testid={testId} className={`rounded-md border p-3 space-y-3 ${invalid ? "border-brand-red" : "border-input"} ${compact ? "mt-2 bg-background/60" : ""}`}>
      {entries.map((e, idx) => (
        <div key={idx} data-testid={`${testId}-entry-${idx}`} className="flex gap-2 items-start">
          <span className="font-mono text-[11px] text-muted-foreground mt-2.5 w-5 shrink-0">{idx + 1}.</span>
          <div className={`flex-1 grid gap-2 ${subs.length > 1 ? "sm:grid-cols-2" : ""}`}>
            {subs.map((s, si) => (
              <div key={si}>
                {subs.length > 1 && <label className="text-[11px] text-muted-foreground mb-1 block first-letter:uppercase">{s.label}</label>}
                {s.input === "date" ? (
                  <DateField testId={`${testId}-entry-${idx}-sub-${si}`} value={e[si] || ""} onChange={(v) => set(idx, si, v)} placeholder={t.docs.pick_date} />
                ) : (
                  <Input data-testid={`${testId}-entry-${idx}-sub-${si}`} value={e[si] || ""} onChange={(ev) => set(idx, si, ev.target.value)} placeholder={subs.length > 1 ? "" : s.label || t.docs.extra_placeholder} className="bg-background" />
                )}
              </div>
            ))}
          </div>
          <button type="button" data-testid={`${testId}-remove-${idx}`} onClick={() => remove(idx)} aria-label={t.docs.list_remove} className="p-2 rounded-full hover:bg-muted text-muted-foreground mt-0.5"><X size={14} /></button>
        </div>
      ))}
      <button type="button" data-testid={`${testId}-add`} onClick={add} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-teal hover:text-brand-tealDark">
        <Plus size={13} /> {t.docs.list_add}
      </button>
    </div>
  );
}
